import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, FortnoxExportAccountSettings as FortnoxExportAccountSettingsType } from '@/types';
import { apiService } from '@/utils/api';

interface FortnoxExportAccountSettingsProps {
  className?: string;
}

const FortnoxExportAccountSettings: React.FC<FortnoxExportAccountSettingsProps> = ({ className }) => {
  const queryClient = useQueryClient();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<FortnoxExportAccountSettingsType>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Fetch sites
  const { data: sites = [], isLoading: sitesLoading } = useQuery(
    ['sites'],
    () => apiService.getSites()
  );

  // Set default site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  // Fetch Fortnox export account settings
  const { data: exportAccountSettings, isLoading: settingsLoading } = useQuery(
    ['fortnox-export-account-settings', selectedSiteId],
    () => apiService.getFortnoxExportAccountSettings(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
    }
  );

  // Update form data when settings are loaded
  useEffect(() => {
    if (exportAccountSettings) {
      setFormData(exportAccountSettings);
    }
  }, [exportAccountSettings]);

  // Save mutation
  const saveMutation = useMutation(
    async (data: FortnoxExportAccountSettingsType) => {
      if (data.id) {
        return apiService.updateFortnoxExportAccountSettings(selectedSiteId!, data);
      } else {
        return apiService.createFortnoxExportAccountSettings(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox Export Account Settings saved' });
        queryClient.invalidateQueries(['fortnox-export-account-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async () => {
      return apiService.deleteFortnoxExportAccountSettings(selectedSiteId!);
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox Export Account Settings deleted' });
        setFormData({});
        queryClient.invalidateQueries(['fortnox-export-account-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSiteChange = (siteId: number) => {
    setSelectedSiteId(siteId);
    setFormData({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiteId) return;
    
    const submitData: FortnoxExportAccountSettingsType = {
      ...formData,
      site_id: selectedSiteId,
    } as FortnoxExportAccountSettingsType;
    
    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      deleteMutation.mutate();
    }
  };

  const isLoading = sitesLoading || settingsLoading;

  return (
    <div className={`container mx-auto px-4 py-8 ${className || ''}`}>
      <h1 className="section-title">Fortnox Export Account Settings</h1>
      
      {/* Loading Alert */}
      {isLoading && (
        <div className="alert alert-info">
          <span>Loading...</span>
        </div>
      )}

      {/* Success/Error Messages */}
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : message.type === 'error' ? 'alert-danger' : 'alert-info'}`}>
          <span>{message.text}</span>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Site Selection */}
          <div>
            <label className="form-label">Site ID:</label>
            <select
              value={selectedSiteId || ''}
              onChange={(e) => handleSiteChange(parseInt(e.target.value))}
              className="form-input"
              disabled={isLoading}
            >
              {sites.map((site: Site) => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          {/* Export Account Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="form-label">Sales NO Account:</label>
              <input
                type="number"
                value={formData.sales_NO_account || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow 4-digit numbers
                  if (value === '' || (value.length <= 4 && /^\d+$/.test(value))) {
                    handleInputChange('sales_NO_account', value === '' ? '' : parseInt(value));
                  }
                }}
                className="form-input"
                disabled={isLoading}
                min="1000"
                max="9999"
                placeholder="4 digits"
              />
            </div>

            <div>
              <label className="form-label">NO VAT account:</label>
              <input
                type="number"
                value={formData.NO_vat_account || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow 4-digit numbers
                  if (value === '' || (value.length <= 4 && /^\d+$/.test(value))) {
                    handleInputChange('NO_vat_account', value === '' ? '' : parseInt(value));
                  }
                }}
                className="form-input"
                disabled={isLoading}
                min="1000"
                max="9999"
                placeholder="4 digits"
              />
            </div>

            <div>
              <label className="form-label">Sales Other Countries Account:</label>
              <input
                type="number"
                value={formData.sales_OTHER_account || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow 4-digit numbers
                  if (value === '' || (value.length <= 4 && /^\d+$/.test(value))) {
                    handleInputChange('sales_OTHER_account', value === '' ? '' : parseInt(value));
                  }
                }}
                className="form-input"
                disabled={isLoading}
                min="1000"
                max="9999"
                placeholder="4 digits"
              />
            </div>

            <div>
              <label className="form-label">Vat Other Countries Account:</label>
              <input
                type="number"
                value={formData.OTHER_vat_account || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow 4-digit numbers
                  if (value === '' || (value.length <= 4 && /^\d+$/.test(value))) {
                    handleInputChange('OTHER_vat_account', value === '' ? '' : parseInt(value));
                  }
                }}
                className="form-input"
                disabled={isLoading}
                min="1000"
                max="9999"
                placeholder="4 digits"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              disabled={isLoading || saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save'}
            </button>
            
            {formData.id && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={isLoading || deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default FortnoxExportAccountSettings;
